"use client";

import React from "react";
import { motion } from "motion/react";
import { ShieldCheck, LogOut, FileText, Database, RefreshCw } from "lucide-react";
import { Section } from "../components/section";
import { SEO } from "../components/seo";
import { useAuth } from "../components/auth-provider";
import { AdminTabs } from "../components/admin/admin-tabs";
import { MessagesManager } from "../components/admin/messages-manager";
import { ProjectsManager } from "../components/admin/projects-manager";
import { BlogManager } from "../components/admin/blog-manager";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { initialProjects, initialPosts } from "../data/initial-data";

export function Admin() {
  const { signOut, user } = useAuth();

  const handleSyncData = async () => {
    const confirmSync = confirm("This will populate your database with the initial projects and blog posts. Continue?");
    if (!confirmSync) return;

    toast.info("Syncing initial data...");
    
    try {
      // Sync Projects
      for (const project of initialProjects) {
        await supabase.from('kv_store_5f13ee94').upsert({
          key: `project:${project.id}`,
          value: project
        });
      }

      // Sync Blog Posts
      for (const post of initialPosts) {
        await supabase.from('kv_store_5f13ee94').upsert({
          key: `post:${post.slug}`,
          value: post
        });
      }

      toast.success("Database synced successfully!", {
        description: "Refresh the page to see the changes in the managers below."
      });
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Failed to sync data");
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-black">
      <SEO title="Admin Console" noindex={true} />
      
      <Section 
        id="admin-dashboard" 
        title="CMS Console" 
        subtitle={`Welcome back, ${user?.email?.split('@')[0] || 'Admin'}.`}
      >
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
          <div className="flex items-center gap-4 text-white">
            <div className="p-3 bg-violet-600/20 rounded-2xl text-violet-400 shadow-[0_0_20px_rgba(139,92,246,0.2)]">
              <ShieldCheck size={28} />
            </div>
            <div>
              <h3 className="text-2xl font-black uppercase tracking-tight">System Control</h3>
              <p className="text-xs font-bold text-neutral-500 uppercase tracking-widest">Authenticated Session</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <button 
              onClick={handleSyncData}
              className="flex items-center gap-2 px-6 py-3 bg-amber-600/10 hover:bg-amber-600/20 text-amber-400 rounded-xl transition-all text-xs font-black uppercase tracking-widest border border-amber-500/20"
            >
              <RefreshCw size={16} /> Sync Initial Data
            </button>
            <a 
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-cyan-600/10 hover:bg-cyan-600/20 text-cyan-400 rounded-xl transition-all text-xs font-black uppercase tracking-widest border border-cyan-500/20"
            >
              <FileText size={16} /> Sitemap
            </a>
            <button 
              onClick={signOut}
              className="flex items-center gap-2 px-6 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-xl transition-all text-xs font-black uppercase tracking-widest border border-red-500/20"
            >
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>

        <AdminTabs 
          messagesContent={<MessagesManager />}
          projectsContent={<ProjectsManager />}
          blogContent={<BlogManager />}
        />
      </Section>
    </div>
  );
}